import {
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
	Save as SaveIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	createFilterOptions,
	Divider,
	FormControl,
	FormControlLabel,
	Grid2,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { isEqual, orderBy } from "lodash";
import moment, { Moment } from "moment";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { IUserRowDetails } from ".";
import { AGDataGrid } from "../../../components/AGDataGrid";
import { AGDialog } from "../../../components/AGDialog";
import NumberInput from "../../../components/NumberInput";
import { useAppSelector } from "../../../redux";
import { useGetJurisdictionsListQuery } from "../../../redux/reducers/jurisdictions.reducer";
import { useGetUserGroupHierarchyQuery, useGetUserRolesQuery } from "../../../redux/reducers/rolesPermissions.reducer";
import { useCreateUserMutation, useEditUserMutation } from "../../../redux/reducers/userManagement.reducer";
import {
	IGetJurisdictionsResponseData,
	JURISDICTION_TYPE_E,
	PERMISSION_TYPE_E,
	PermissionDetailsType
} from "../../../types";
import { permissionMapping, VALIDATION_REGEX } from "../../../utils/constants";
import ChangePasswordDialog from "./ChangePasswordDialog";

interface IUserDetailsDialogProps {
	open: boolean;
	userDetails: IUserRowDetails | null;
	onClose: () => void;
}

interface IUserFormDetails {
	first_name: string;
	last_name: string;
	username: string;
	mobile_number: string;
	email: string;
	role: number;
	is_active: boolean;
	designation: string;
	validity_start_date: Moment;
	validity_end_date: Moment;
	rto?: number[];
	employee_id: string;
	permissions: PermissionDetailsType;
	useDefaultPermissions: boolean;
	challan_user_id: number;
	district_id: number;
}

enum SELECTED_TAB_E {
	DETAILS,
	PERMISSIONS
}

interface IPermissionRowDetails {
	id: PERMISSION_TYPE_E;
	name: string;
	view: boolean;
	edit: boolean;
	viewDisabled: boolean;
	editDisabled: boolean;
}

const DEFAULT_PERMISSIONS: PermissionDetailsType = {
	[PERMISSION_TYPE_E.USER_MANAGEMENT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.DASHBOARD]: { view: false, edit: false },
	[PERMISSION_TYPE_E.VIOLATION_LOGS]: { view: false, edit: false },
	[PERMISSION_TYPE_E.RTO_METADATA]: { view: false, edit: false },
	[PERMISSION_TYPE_E.INTERCEPTOR_VEHICLES]: { view: false, edit: false },
	[PERMISSION_TYPE_E.USER_SESSION_MANAGEMENT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.RESET_USER_ACCOUNT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.USER_ACTIVITY_LOGS]: { view: false, edit: false },
	[PERMISSION_TYPE_E.SYSTEM_CONFIGURATION]: { view: false, edit: false }
};

const INVALID_VIEW_PERMISSIONS: PERMISSION_TYPE_E[] = [PERMISSION_TYPE_E.RESET_USER_ACCOUNT];
const INVALID_EDIT_PERMISSIONS: PERMISSION_TYPE_E[] = [PERMISSION_TYPE_E.USER_ACTIVITY_LOGS];

const UserDetailsDialog: FC<IUserDetailsDialogProps> = (props) => {
	const { open, userDetails, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	const [selectedTab, setSelectedTab] = useState<SELECTED_TAB_E>(SELECTED_TAB_E.DETAILS);
	const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState<boolean>(false);

	const {
		reset,
		watch,
		control,
		register,
		setValue,
		handleSubmit,
		formState: { errors, isDirty: inputChanged }
	} = useForm<IUserFormDetails>();

	const status = watch("is_active") ?? false;
	const role = watch("role");
	const selectedRTOs = watch("rto") ?? [];
	const permissions = watch("permissions");
	const useDefaultPermissions = watch("useDefaultPermissions");

	// APIS
	// GET JURISDICTIONS LIST
	const {
		data: jurisdictionsList,
		isLoading: getJurisdictionsListLoading,
		isFetching: getJurisdictionsListFetching
	} = useGetJurisdictionsListQuery({ all: true, jurisdiction_type: JURISDICTION_TYPE_E.RTOS });

	// CREATE USER
	const [createUser, { isLoading: createUserLoading, error: createUserError, reset: resetCreateUserState }] =
		useCreateUserMutation();

	// EDIT USER
	const [editUser, { isLoading: editUserLoading, error: editUserError, reset: resetEditUserState }] =
		useEditUserMutation();

	// GET USER ROLES
	const { data: getUserGroupsResponse } = useGetUserRolesQuery({});

	// GET USER GROUP HIERARCHY
	const { data: getUserGroupHierarchyResponse } = useGetUserGroupHierarchyQuery();

	const filterOptions = createFilterOptions<IGetJurisdictionsResponseData>({
		matchFrom: "any",
		ignoreAccents: true,
		ignoreCase: true,
		stringify: (option) => JSON.stringify(option)
	});

	const rowsData = useMemo<IPermissionRowDetails[]>(() => {
		if (!permissions) return [];

		const userPermissions = authState.user.permissions;

		return Object.entries(permissions).map(([key, details]) => ({
			id: key as PERMISSION_TYPE_E,
			name: permissionMapping[key as PERMISSION_TYPE_E],
			view: details.view,
			edit: details.edit,
			viewDisabled:
				INVALID_VIEW_PERMISSIONS.includes(key as PERMISSION_TYPE_E) ||
				!userPermissions?.[key as PERMISSION_TYPE_E]?.view,
			editDisabled:
				INVALID_EDIT_PERMISSIONS.includes(key as PERMISSION_TYPE_E) ||
				!userPermissions?.[key as PERMISSION_TYPE_E]?.edit
		}));
	}, [authState.user.permissions, permissions]);

	const userRolesList = useMemo(
		() => getUserGroupHierarchyResponse?.find((item) => item.group.id === authState.user.group_id)?.children ?? [],
		[authState.user.group_id, getUserGroupHierarchyResponse]
	);

	const readOnlyInput = useMemo(
		() => !authState.user.permissions || !authState.user.permissions.user_management.edit,
		[authState.user.permissions]
	);

	const selectedRoleDetails = useMemo(
		() => userRolesList.find((item) => item.id === role) ?? null,
		[role, userRolesList]
	);

	function handleChangeTab(tab: SELECTED_TAB_E) {
		setSelectedTab(tab);
	}

	function handleSaveButtonClick(formData: IUserFormDetails) {
		const updatedPermissions = { ...formData.permissions };
		const userPermissions = authState.user.permissions;

		for (const [key, permissionDetails] of Object.entries(formData.permissions)) {
			const permissionKey = key as PERMISSION_TYPE_E;

			if (permissionDetails.view && !userPermissions?.[permissionKey].view) {
				updatedPermissions[permissionKey].view = false;
			}

			if (permissionDetails.edit && !userPermissions?.[permissionKey].edit) {
				updatedPermissions[permissionKey].edit = false;
			}
		}

		if (userDetails) {
			editUser({
				id: userDetails.id,
				data: {
					first_name: formData.first_name,
					last_name: formData.last_name,
					username: formData.username,
					email: formData.email,
					phone_number: formData.mobile_number,
					designation: formData.designation,
					is_active: formData.is_active,
					enable_ai_search: false,
					user_permissions: isEqual(userDetails.permissions, updatedPermissions) ? undefined : updatedPermissions,
					jurisdiction: formData.rto ?? [1],
					groups: [formData.role],
					validity_start_date: formData.validity_start_date.toISOString(),
					validity_end_date: formData.validity_end_date.toISOString(),
					use_group_permissions_only: formData.useDefaultPermissions,
					employee_id: formData.employee_id,
					challan_user_id: formData.challan_user_id,
					district_id: formData.district_id
				}
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		} else {
			createUser({
				first_name: formData.first_name,
				last_name: formData.last_name,
				username: formData.username,
				email: formData.email,
				phone_number: formData.mobile_number,
				designation: formData.designation,
				is_active: formData.is_active,
				enable_ai_search: false,
				user_permissions: updatedPermissions,
				jurisdiction: formData.rto ?? [1],
				groups: [formData.role],
				validity_start_date: formData.validity_start_date.toISOString(),
				validity_end_date: formData.validity_end_date.toISOString(),
				use_group_permissions_only: formData.useDefaultPermissions,
				employee_id: formData.employee_id,
				challan_user_id: formData.challan_user_id,
				district_id: formData.district_id
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		}
	}

	function handleOpenChangePasswordDialog() {
		setChangePasswordDialogOpen(true);
	}

	function handleCloseChangePasswordDialog() {
		setChangePasswordDialogOpen(false);
	}

	useEffect(() => {
		if (open) {
			const jurisdictionIds = (jurisdictionsList?.results ?? []).map((jurisdiction) => jurisdiction.id);
			reset({
				first_name: userDetails?.first_name ?? "",
				last_name: userDetails?.last_name ?? "",
				username: userDetails?.username ?? "",
				mobile_number: userDetails?.mobile_number ?? "",
				email: userDetails?.email ?? "",
				role: userDetails?.role ?? 0,
				is_active: userDetails?.is_active ?? true,
				designation: userDetails?.designation ?? "",
				rto: userDetails?.rto?.filter((rtoId) => jurisdictionIds.includes(rtoId)),
				employee_id: userDetails?.employee_id ?? "",
				validity_start_date:
					userDetails && userDetails.validity_start_date ? moment(userDetails.validity_start_date) : moment(),
				validity_end_date:
					userDetails && userDetails.validity_end_date ? moment(userDetails.validity_end_date) : moment().endOf("year"),
				permissions: userDetails?.permissions ?? DEFAULT_PERMISSIONS,
				useDefaultPermissions: userDetails?.use_group_permissions_only ?? true,
				challan_user_id: userDetails?.challan_user_id,
				district_id: userDetails?.district_id
			});
		} else {
			resetCreateUserState();
			resetEditUserState();
			setSelectedTab(SELECTED_TAB_E.DETAILS);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, userDetails, jurisdictionsList]);

	useEffect(() => {
		if (getUserGroupsResponse && role > 0 && useDefaultPermissions) {
			const defaultPermissions = getUserGroupsResponse.find((item) => item.id === role)?.group_permissions;
			if (defaultPermissions) setValue("permissions", defaultPermissions);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getUserGroupsResponse, role, useDefaultPermissions]);

	const columnsData: GridColDef[] = [
		{
			field: "name",
			headerName: "Permission",
			flex: 1,
			sortable: false,
			cellClassName: useDefaultPermissions ? "disabled" : ""
		},
		{
			field: "view",
			headerName: "View",
			width: 75,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params: GridRenderCellParams<IPermissionRowDetails, boolean>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Controller
						control={control}
						name={`permissions.${params.row.id}.view`}
						render={({ field: { value, onChange } }) => (
							<Checkbox
								checked={value}
								disabled={useDefaultPermissions || params.row.viewDisabled}
								onChange={readOnlyInput ? undefined : (_event, checked) => onChange(checked)}
							/>
						)}
					/>
				</Box>
			)
		},
		{
			field: "edit",
			headerName: "Edit",
			width: 75,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params: GridRenderCellParams<IPermissionRowDetails, boolean>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Controller
						control={control}
						name={`permissions.${params.row.id}.edit`}
						render={({ field: { value, onChange } }) => (
							<Checkbox
								checked={value}
								disabled={useDefaultPermissions || params.row.editDisabled}
								onChange={readOnlyInput ? undefined : (_event, checked) => onChange(checked)}
							/>
						)}
					/>
				</Box>
			)
		}
	];

	const TabRenderer: { [key in SELECTED_TAB_E]: ReactNode } = {
		[SELECTED_TAB_E.DETAILS]: (
			<Grid2 container spacing={2} sx={{ marginTop: "1.5rem" }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="First Name"
						autoComplete="off"
						error={!!errors.first_name}
						helperText={errors.first_name?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("first_name", {
							required: "First name is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Last Name"
						autoComplete="off"
						error={!!errors.last_name}
						helperText={errors.last_name?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("last_name", {
							required: "Last name is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Email"
						autoComplete="off"
						error={!!errors.email}
						helperText={errors.email?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("email", {
							required: "Email is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<NumberInput
						required
						fullWidth
						isContactNumber
						size="small"
						label="Mobile Number"
						error={!!errors.mobile_number}
						helperText={errors.mobile_number?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("mobile_number", {
							required: "Mobile number is required",
							pattern: { value: VALIDATION_REGEX.MOBILE_NUMBER, message: "Invalid mobile number" }
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Employee ID"
						autoComplete="off"
						error={!!errors.employee_id}
						helperText={errors.employee_id?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("employee_id", {
							required: "Employee ID is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Designation"
						autoComplete="off"
						error={!!errors.designation}
						helperText={errors.designation?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("designation", {
							required: "Designation is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Username"
						autoComplete="off"
						error={!!errors.username}
						helperText={errors.username?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("username", {
							required: "Username is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="is_active"
						render={({ field: { value, onChange } }) => (
							<FormControl required fullWidth size="small">
								<InputLabel htmlFor="status-input">Status</InputLabel>
								<Select
									label="Status"
									id="status-input"
									value={Number(value)}
									onChange={(event) => onChange(Boolean(event.target.value))}
									input={<OutlinedInput label="Status" />}
									readOnly={readOnlyInput}
									startAdornment={
										<Box
											sx={{
												width: 10,
												height: 10,
												borderRadius: "50%",
												bgcolor: status ? "#43A047" : "#BDBDBD",
												mr: 1
											}}
										/>
									}
								>
									<MenuItem value={1}>Active</MenuItem>
									<MenuItem value={0}>Inactive</MenuItem>
								</Select>
							</FormControl>
						)}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<NumberInput
						required
						fullWidth
						positiveOnly
						disableSigned
						disableDecimal
						size="small"
						label="Challan User ID"
						error={!!errors.challan_user_id}
						helperText={errors.challan_user_id?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("challan_user_id")}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<NumberInput
						required
						fullWidth
						positiveOnly
						disableSigned
						disableDecimal
						size="small"
						label="District ID"
						error={!!errors.district_id}
						helperText={errors.district_id?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("district_id")}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="role"
						render={({ field: { value, onChange } }) => (
							<FormControl required fullWidth size="small">
								<InputLabel htmlFor="role-input">Role</InputLabel>

								<Select
									label="Role"
									id="role-input"
									input={<OutlinedInput label="Role" />}
									value={value}
									onChange={onChange}
									readOnly={readOnlyInput}
								>
									{userRolesList.length > 0 ? (
										orderBy(userRolesList ?? [], "id").map((userGroupItem) => (
											<MenuItem key={userGroupItem.id} value={userGroupItem.id}>
												{userGroupItem.name}
											</MenuItem>
										))
									) : (
										<MenuItem disabled>No roles found</MenuItem>
									)}
								</Select>
							</FormControl>
						)}
					/>
				</Grid2>

				{selectedRoleDetails && !selectedRoleDetails.has_all_rto_access ? (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Controller
							control={control}
							name="rto"
							render={({ field: { value, onChange } }) => (
								<Autocomplete
									fullWidth
									multiple
									disableCloseOnSelect
									limitTags={1}
									options={jurisdictionsList?.results ?? []}
									getOptionLabel={(option) => option.rto_code || ""}
									getOptionKey={(option) => option.id}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									value={jurisdictionsList?.results?.filter((item) => value?.includes(item.id)) ?? []}
									onChange={(_event, value) => onChange(value?.map((item) => item.id) ?? [])}
									filterOptions={filterOptions}
									readOnly={readOnlyInput}
									renderOption={({ key, ...props }, option, { selected }) => (
										<li key={key} {...props} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
											{selected ? (
												<CheckBoxIcon color="primary" sx={{ marginY: "0.25rem" }} />
											) : (
												<CheckBoxOutlineBlankIcon color="primary" sx={{ marginY: "0.25rem" }} />
											)}

											<Typography>{option.rto_code}</Typography>
											<Typography variant="body2" color="textSecondary">
												{option.name}
											</Typography>
										</li>
									)}
									loading={getJurisdictionsListLoading || getJurisdictionsListFetching}
									renderTags={() => <></>}
									renderInput={(params) => (
										<TextField
											{...params}
											fullWidth
											size="small"
											label="RTO"
											error={!!errors.rto}
											helperText={errors.rto?.message?.toString() || ""}
										/>
									)}
									PaperComponent={(props) => {
										const { children, ...restProps } = props;
										const AllRTOSelected =
											value?.length === 0
												? "none"
												: value?.length === jurisdictionsList?.results?.length
												? "all"
												: "partial";

										return (
											<Paper {...restProps}>
												<Box
													onMouseDown={(event) => event.preventDefault()}
													sx={{ paddingX: "1.25rem", paddingY: "0.25rem", width: "100%" }}
												>
													<FormControlLabel
														label="Select All"
														sx={{ width: "100%" }}
														control={
															<Checkbox
																checked={AllRTOSelected === "all"}
																indeterminate={AllRTOSelected === "partial"}
															/>
														}
														onClick={(event) => {
															event.preventDefault();
															if (AllRTOSelected === "none" || AllRTOSelected === "partial") {
																onChange(jurisdictionsList?.results?.map((item) => item.id) ?? []);
															} else if (AllRTOSelected === "all") {
																onChange([]);
															}
														}}
													/>
												</Box>

												<Divider />

												{children}
											</Paper>
										);
									}}
								/>
							)}
						/>

						<Typography sx={{ marginTop: selectedRTOs.length === 0 ? "0.5rem" : "0.5rem" }}>
							{selectedRTOs.length === jurisdictionsList?.results?.length
								? "All RTOs selected"
								: selectedRTOs.length === 0
								? "No RTO selected"
								: `${selectedRTOs.length} RTO${selectedRTOs.length !== 1 ? "s" : ""} selected`}
						</Typography>
					</Grid2>
				) : null}

				<Grid2 size={{ xs: 12 }}>
					<Typography color="text.secondary" fontWeight={500}>
						User Validity
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="validity_start_date"
						rules={{ required: "Validity start date is required" }}
						render={({ field: { value, onChange } }) => (
							<DatePicker
								label="Validity Start Date"
								format="DD/MM/YYYY"
								value={value}
								onChange={onChange}
								readOnly={readOnlyInput}
								slotProps={{
									textField: {
										required: true,
										fullWidth: true,
										size: "small",
										error: !!errors.validity_start_date,
										helperText: errors.validity_start_date?.message?.toString() || ""
									}
								}}
							/>
						)}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="validity_end_date"
						rules={{ required: "Validity end date is required" }}
						render={({ field: { value, onChange } }) => (
							<DatePicker
								label="Validity End Date"
								format="DD/MM/YYYY"
								value={value}
								onChange={onChange}
								readOnly={readOnlyInput}
								slotProps={{
									textField: {
										required: true,
										fullWidth: true,
										size: "small",
										error: !!errors.validity_end_date,
										helperText: errors.validity_end_date?.message?.toString() || ""
									}
								}}
							/>
						)}
					/>
				</Grid2>
			</Grid2>
		),
		[SELECTED_TAB_E.PERMISSIONS]: (
			<Box sx={{ marginTop: "1rem" }}>
				<Controller
					control={control}
					name="useDefaultPermissions"
					render={({ field: { value, onChange } }) => (
						<FormControlLabel
							control={<Switch />}
							label="Use default permissions of user role"
							checked={value}
							onChange={(_event, checked) => onChange(checked)}
							sx={{ marginBottom: "0.5rem" }}
						/>
					)}
				/>

				<AGDataGrid
					hideFooter
					hideAllSeparators
					columns={columnsData}
					rows={rowsData}
					getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 1 ? "gray-background" : "")}
				/>
			</Box>
		)
	};

	return (
		<AGDialog open={open} onClose={onClose} title={userDetails ? "Edit User" : "Add New User"} color="primary">
			<Box>
				<Box className="user-tabs-wrapper">
					<Box
						className={`user-tab-item ${selectedTab === SELECTED_TAB_E.DETAILS ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.DETAILS)}
					>
						<Typography textAlign="center">User Details</Typography>
					</Box>

					<Box
						className={`user-tab-item ${selectedTab === SELECTED_TAB_E.PERMISSIONS ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.PERMISSIONS)}
					>
						<Typography textAlign="center">Permissions</Typography>
					</Box>
				</Box>

				{TabRenderer[selectedTab]}

				{createUserError ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{"data" in createUserError && createUserError.data
							? String(createUserError.data)
							: "Failed to add new user"}
					</Typography>
				) : null}

				{editUserError ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{"data" in editUserError && editUserError.data ? String(editUserError.data) : "Failed to edit user"}
					</Typography>
				) : null}

				{selectedTab === SELECTED_TAB_E.PERMISSIONS && Object.keys(errors).length > 0 ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{Object.values(errors)[0]?.message?.toString() || "Something went wrong"}
					</Typography>
				) : null}

				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "1rem" }}>
					{userDetails ? (
						<Button disableElevation variant="contained" color="primary" onClick={handleOpenChangePasswordDialog}>
							Change Password
						</Button>
					) : (
						<Box />
					)}

					{!readOnlyInput ? (
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							<Button variant="outlined" onClick={onClose}>
								Cancel
							</Button>

							<LoadingButton
								disableElevation
								variant="contained"
								color="success"
								startIcon={<SaveIcon />}
								onClick={handleSubmit(handleSaveButtonClick)}
								loading={createUserLoading || editUserLoading}
								loadingPosition="start"
								disabled={!inputChanged}
							>
								Save
							</LoadingButton>
						</Box>
					) : null}
				</Box>
			</Box>

			<ChangePasswordDialog
				open={changePasswordDialogOpen}
				userId={userDetails?.id ?? 0}
				onClose={handleCloseChangePasswordDialog}
			/>
		</AGDialog>
	);
};

export default UserDetailsDialog;
